import type React from 'react';
import { observer } from 'mobx-react';
import type { ITwoDLayerController } from '@/engine/app/interfaces';
import type { ITwoDViewModel } from '@/engine/UIPresentation/Interfaces';
import styled from 'styled-components';
import { CustomSVGLine } from './CustomSVGLine';
import { Tooltip } from './Tooltip';

export const TwoDLayer: React.FC<{
  controller: ITwoDLayerController;
  isMaximized?: boolean;
}> = observer((props) => {
  const twoDViewModels: Array<ITwoDViewModel> = props.controller.twoDViewModels;
  const isMaximized: boolean | undefined = props.isMaximized;

  return (
    <>
      <SVGElement>
        {twoDViewModels
          .filter((v) => v.isVisible)
          .map((v) => {
            return <CustomSVGLine attachedLine={v.attachedLineProps} point2D={v.point2D} key={v.id.toString()} />;
          })}
      </SVGElement>
      <Container $isMaximized={isMaximized} tabIndex={0}>
        {twoDViewModels
          .filter((v) => v.isVisible)
          .map((v) => {
            const PresentationComponent = v.reactComponent;
            return (
              <PresentationComponent
                descriptor={v.descriptor}
                isVisible={v.isVisible}
                label={v.label}
                isSelected={v.isSelected}
                isHighlighted={v.isHighlighted}
                attachedLineProps={v.attachedLineProps}
                details={v.details}
                value={v.value}
                suffix={v.suffix}
                point={v.point2D}
                color={v.color}
                isSlightlyDisplaced={v.isSlightlyDisplaced}
                key={v.id.toString()}
                dataTestId={v.dataTestId}
                kind={v.kind}
                setValue={v.setValue}
              ></PresentationComponent>
            );
          })}
        {props.controller.tooltips.map((tooltip) => {
          if (!tooltip.isShown) {
            return null;
          }
          return (
            <Tooltip
              key={tooltip.id.toString()}
              text={tooltip.text}
              descriptor={tooltip.descriptor}
              placement={tooltip.placement}
              onDismiss={(): void => props.controller.hideTooltip(tooltip)}
            />
          );
        })}
      </Container>
    </>
  );
});

const Container = styled.div<{ $isMaximized?: boolean }>`
  font-size: ${({ theme }): string => (theme.mode === 'report' ? '1.5em' : '1em')};
  position: absolute;
  width: 100%;
  height: 100%;
  padding-bottom: ${(props): string => (props.$isMaximized ? '5em' : '0')};
  padding-left: ${(props): string => (props.$isMaximized ? '1em' : '0')};
  pointer-events: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: flex-end;
  z-index: 9;
`;

const SVGElement = styled.svg`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 5;
  pointer-events: none;
`;
